/**
 * Advanced Scheduling Utilities for Project Asset Checkpoints
 * 
 * Utility functions for managing custom scheduling configurations,
 * calendar views, and schedule calculations.
 */

export type Frequency = 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
export type ExecutionType = 'one-time' | 'repeated';
export type ScheduleType = 'auto' | 'custom';

export interface CustomSchedule {
  startDate?: string;
  endDate?: string;
  executionType: ExecutionType;
  customFrequency?: Frequency;
  weeklyDays?: number[]; // 0=Sunday, 1=Monday, etc.
  monthlyDay?: number; // 1-31
  yearlyMonths?: number[]; // 0=January, 1=February, etc.
  executionTime?: string; // HH:MM format
  timezone?: string;
  skipWeekends?: boolean;
  skipHolidays?: boolean;
  holidayCalendar?: string;
}

export interface SchedulingConfig {
  scheduleType: ScheduleType;
  customSchedule?: CustomSchedule;
}

/**
 * Get default scheduling configuration
 */
export const getDefaultSchedulingConfig = (): SchedulingConfig => ({
  scheduleType: 'auto',
  customSchedule: {
    executionType: 'repeated',
    executionTime: '01:00', // Default to 1:00 AM
    timezone: 'Asia/Kolkata', // Indian Standard Time (IST)
    skipWeekends: false,
    skipHolidays: false,
  }
});

/**
 * Get day names for week selection
 */
export const getDayNames = (): Array<{ value: number; label: string; short: string }> => [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

/**
 * Get month names for year selection
 */
export const getMonthNames = (): Array<{ value: number; label: string; short: string }> => [
  { value: 0, label: 'January', short: 'Jan' },
  { value: 1, label: 'February', short: 'Feb' },
  { value: 2, label: 'March', short: 'Mar' },
  { value: 3, label: 'April', short: 'Apr' },
  { value: 4, label: 'May', short: 'May' },
  { value: 5, label: 'June', short: 'Jun' },
  { value: 6, label: 'July', short: 'Jul' },
  { value: 7, label: 'August', short: 'Aug' },
  { value: 8, label: 'September', short: 'Sep' },
  { value: 9, label: 'October', short: 'Oct' },
  { value: 10, label: 'November', short: 'Nov' },
  { value: 11, label: 'December', short: 'Dec' },
];

/**
 * Get frequency options for custom scheduling
 */
export const getFrequencyOptions = (): Array<{ value: Frequency; label: string }> => [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Fortnightly', label: 'Fortnightly (Every 2 weeks)' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly (Every 3 months)' },
  { value: 'Half-Yearly', label: 'Half-Yearly (Every 6 months)' },
  { value: 'Yearly', label: 'Yearly' },
];

/**
 * Get execution type options
 */
export const getExecutionTypeOptions = (): Array<{ value: ExecutionType; label: string; description: string }> => [
  { 
    value: 'one-time', 
    label: 'One Time', 
    description: 'Execute this checkpoint only once' 
  },
  { 
    value: 'repeated', 
    label: 'Repeated', 
    description: 'Execute this checkpoint repeatedly based on schedule' 
  },
];

/**
 * Calculate next execution date based on custom schedule
 */
export const calculateNextExecutionDate = (
  customSchedule: CustomSchedule,
  fromDate: Date = new Date()
): Date | null => {
  if (!customSchedule.startDate || customSchedule.executionType === 'one-time') {
    return customSchedule.startDate ? new Date(customSchedule.startDate) : null;
  }

  const startDate = new Date(customSchedule.startDate);
  if (fromDate < startDate) {
    return startDate;
  }

  // Check if we have an end date and it has passed
  if (customSchedule.endDate) {
    const endDate = new Date(customSchedule.endDate);
    if (fromDate >= endDate) {
      return null;
    }
  }

  const frequency = customSchedule.customFrequency || 'Weekly';
  
  switch (frequency) {
    case 'Daily':
      return calculateNextDailyExecution(customSchedule, fromDate);
    
    case 'Weekly':
      return calculateNextWeeklyExecution(customSchedule, fromDate);
    
    case 'Fortnightly':
      return calculateNextFortnightlyExecution(customSchedule, fromDate);
    
    case 'Monthly':
      return calculateNextMonthlyExecution(customSchedule, fromDate);
    
    case 'Quarterly':
      return calculateNextQuarterlyExecution(customSchedule, fromDate);
    
    case 'Half-Yearly':
      return calculateNextHalfYearlyExecution(customSchedule, fromDate);
    
    case 'Yearly':
      return calculateNextYearlyExecution(customSchedule, fromDate);
    
    default:
      return null;
  }
};

/**
 * Calculate next daily execution
 */
const calculateNextDailyExecution = (customSchedule: CustomSchedule, fromDate: Date): Date => {
  const nextDate = new Date(fromDate);
  nextDate.setDate(nextDate.getDate() + 1);
  
  if (customSchedule.skipWeekends) {
    while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
  }
  
  return nextDate;
};

/**
 * Calculate next weekly execution
 */
const calculateNextWeeklyExecution = (customSchedule: CustomSchedule, fromDate: Date): Date => {
  if (!customSchedule.weeklyDays || customSchedule.weeklyDays.length === 0) {
    // Default to same day of week
    const nextDate = new Date(fromDate);
    nextDate.setDate(nextDate.getDate() + 7);
    return nextDate;
  }

  // Find next occurrence of any selected day
  for (let i = 1; i <= 7; i++) {
    const nextDate = new Date(fromDate);
    nextDate.setDate(nextDate.getDate() + i);
    
    if (customSchedule.weeklyDays.includes(nextDate.getDay())) {
      return nextDate;
    }
  }

  // Fallback to next week
  const nextDate = new Date(fromDate);
  nextDate.setDate(nextDate.getDate() + 7);
  return nextDate;
};

/**
 * Calculate next fortnightly execution
 */
const calculateNextFortnightlyExecution = (customSchedule: CustomSchedule, fromDate: Date): Date => {
  const nextDate = new Date(fromDate);
  nextDate.setDate(nextDate.getDate() + 14);
  
  if (customSchedule.skipWeekends) {
    while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
  }
  
  return nextDate;
};

/**
 * Calculate next monthly execution
 */
const calculateNextMonthlyExecution = (customSchedule: CustomSchedule, fromDate: Date): Date => {
  const nextDate = new Date(fromDate);
  
  if (customSchedule.monthlyDay) {
    nextDate.setMonth(nextDate.getMonth() + 1);
    nextDate.setDate(customSchedule.monthlyDay);
  } else {
    // Same day of month
    nextDate.setMonth(nextDate.getMonth() + 1);
  }
  
  return nextDate;
};

/**
 * Calculate next quarterly execution
 */
const calculateNextQuarterlyExecution = (customSchedule: CustomSchedule, fromDate: Date): Date => {
  const nextDate = new Date(fromDate);
  nextDate.setMonth(nextDate.getMonth() + 3);
  return nextDate;
};

/**
 * Calculate next half-yearly execution
 */
const calculateNextHalfYearlyExecution = (customSchedule: CustomSchedule, fromDate: Date): Date => {
  const nextDate = new Date(fromDate);
  nextDate.setMonth(nextDate.getMonth() + 6);
  return nextDate;
};

/**
 * Calculate next yearly execution
 */
const calculateNextYearlyExecution = (customSchedule: CustomSchedule, fromDate: Date): Date => {
  const nextDate = new Date(fromDate);
  
  if (customSchedule.yearlyMonths && customSchedule.yearlyMonths.length > 0) {
    // Find next occurrence in specified months
    for (const month of customSchedule.yearlyMonths) {
      const candidateDate = new Date(fromDate);
      candidateDate.setFullYear(candidateDate.getFullYear() + 1);
      candidateDate.setMonth(month);
      candidateDate.setDate(1);
      
      if (candidateDate > fromDate) {
        return candidateDate;
      }
    }
  }
  
  nextDate.setFullYear(nextDate.getFullYear() + 1);
  return nextDate;
};

/**
 * Generate execution dates for a given period
 */
export const generateExecutionDates = (
  customSchedule: CustomSchedule,
  startDate: Date,
  endDate: Date
): Date[] => {
  const dates: Date[] = [];
  let currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    const nextDate = calculateNextExecutionDate(customSchedule, currentDate);
    if (!nextDate || nextDate > endDate) {
      break;
    }
    
    dates.push(new Date(nextDate));
    currentDate = new Date(nextDate);
    currentDate.setDate(currentDate.getDate() + 1); // Move to next day to avoid infinite loop
  }
  
  return dates;
};

/**
 * Validate custom schedule configuration
 */
export const validateCustomSchedule = (customSchedule: CustomSchedule): string[] => {
  const errors: string[] = [];
  
  if (!customSchedule.startDate) {
    errors.push('Start date is required for custom scheduling');
  }
  
  if (customSchedule.executionType === 'repeated' && !customSchedule.customFrequency) {
    errors.push('Custom frequency is required for repeated execution');
  }
  
  if (customSchedule.endDate && customSchedule.startDate) {
    const start = new Date(customSchedule.startDate);
    const end = new Date(customSchedule.endDate);
    if (end <= start) {
      errors.push('End date must be after start date');
    }
  }
  
  if (customSchedule.customFrequency === 'Weekly' && 
      (!customSchedule.weeklyDays || customSchedule.weeklyDays.length === 0)) {
    errors.push('At least one day of the week must be selected for weekly scheduling');
  }
  
  if (customSchedule.monthlyDay && (customSchedule.monthlyDay < 1 || customSchedule.monthlyDay > 31)) {
    errors.push('Monthly day must be between 1 and 31');
  }
  
  if (customSchedule.executionTime && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(customSchedule.executionTime)) {
    errors.push('Execution time must be in HH:MM format');
  }
  
  return errors;
};

/**
 * Format schedule description for display
 */
export const formatScheduleDescription = (schedulingConfig: SchedulingConfig): string => {
  if (schedulingConfig.scheduleType === 'auto') {
    return 'Auto (Based on master checkpoint frequency)';
  }
  
  const customSchedule = schedulingConfig.customSchedule;
  if (!customSchedule) {
    return 'Custom (Not configured)';
  }
  
  const parts: string[] = [];
  
  if (customSchedule.startDate) {
    parts.push(`Start: ${new Date(customSchedule.startDate).toLocaleDateString()}`);
  }
  
  if (customSchedule.endDate) {
    parts.push(`End: ${new Date(customSchedule.endDate).toLocaleDateString()}`);
  }
  
  parts.push(`Type: ${customSchedule.executionType === 'one-time' ? 'One Time' : 'Repeated'}`);
  
  if (customSchedule.customFrequency) {
    parts.push(`Frequency: ${customSchedule.customFrequency}`);
  }
  
  if (customSchedule.executionTime) {
    parts.push(`Time: ${customSchedule.executionTime}`);
  }
  
  return parts.join(' • ');
};
